import { IGlobalStore } from 'src/types/store'
import {
  TStateCommunications,
  createStateSagaCommunications,
} from 'src/helpers/redux'
import { actions as sagaActions, PREFIX } from './actions'

import * as sessionService from 'src/services/session'
import * as localStorageService from 'src/services/localStorage'
import { IGetBillingStatData } from 'src/services/auth/partner'
import { IPartner } from 'src/services/auth/partner/type'
import { IOauthClient } from 'src/services/auth/oauth'
import { getPartner } from 'src/services/location'

export interface IStateData {
  accessToken?: sessionService.ISession['accessToken']
  userId?: sessionService.ISession['userId']
  user?: sessionService.ISession['user']
  isAfterLogin?: boolean
  isUpdatePermissions?: boolean

  billingStat?: IGetBillingStatData
  partnerData?: IPartner
  appView?: sessionService.AppView
  oauthClients: IOauthClient[]
}

export interface IConnectedAccount {
  id: number
  userId: number
  identifier: string
  internalId: number
  driver: string
  status: string
  createdAt: string
  updatedAt: string
  number?: {
    id?: number
    internalId?: number
    partnerGlobalId?: null
    driver?: string
    regionCode?: string
    phoneNumber?: string
    displayName?: string
    identifier?: string
    active?: boolean
    syncAt?: string
    createdAt?: string
    updatedAt?: string
    status?: string
  }
  userIdentifier: {
    id: number
    info: {
      avatar: string
      firstName: string
      jobTitle: string
      lastName: string
    }
  }
}

export interface IUser {
  id: number
  info: {
    jobTitle?: string
    lastName?: string
    firstName?: string
    avatar?: string
  } | null
  groups: IRole[]
  isActive: boolean
  isVerified: boolean
  invitedBy: {
    id: number
    info: {
      firstName?: string
      jobTitle: string
      avatar: string
      lastName?: string
    }
  } | null
  identifier?: string
  partner?: {
    name?: string
    subdomain?: string
  }
  createdAt?: Date
}

export interface IRole {
  slug: string
  description: string
  createdAt: string
  updatedAt: string
  permissions: string[]
}

export interface IState {
  data: IStateData
  accounts: IConnectedAccount[]
  communication: TStateCommunications<typeof sagaActions>
  allUsers: { data: IUser[]; nextCursor: string | null }
  usersWithSearch: { data: IUser[]; nextCursor: string | null }
  roles: IRole[]
}

export const getInitialData = (): IStateData => {
  const session = sessionService.getSession()
  const storage = localStorageService.getStorage()
  const appView = sessionService.getPartnerSession()
  const partner = getPartner()

  const isLocalhost = window.location.hostname === 'localhost'
  const redirectUri = isLocalhost
    ? 'https://localhost:3000/callback'
    : `https://${partner}.${window.settings.APP_DOMAIN}/callback`

  return {
    accessToken: session.accessToken,
    userId: session.userId,
    user: storage.user,
    appView,
    oauthClients: [
      {
        id: window.settings.OAUTH_CLIENT_ID,
        name: 'Platform Dashboard Local',
        isConfidential: false,
        redirectUris: [redirectUri],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
  }
}

export const initialState: IState = {
  data: getInitialData(),
  accounts: [],
  communication: createStateSagaCommunications(sagaActions),
  allUsers: { data: [], nextCursor: null },
  usersWithSearch: { data: [], nextCursor: null },
  roles: [],
}

export type TGlobalStateWithService = IGlobalStore & {
  [PREFIX]: IState
}

export { PREFIX }
